// Raw helpers for the /admin route.
// Uses plain fetch — NO Supabase JS client, NO BroadcastChannel, NO shared auth state.

const SUPABASE_URL  = import.meta.env.VITE_SUPABASE_URL as string
const SUPABASE_KEY  = import.meta.env.VITE_SUPABASE_ANON_KEY as string
const SESSION_KEY   = 'admin-jwt'

export async function adminSignIn(email: string, password: string) {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: SUPABASE_KEY },
    body: JSON.stringify({ email, password }),
  })
  const data = await res.json()
  if (!res.ok) return { error: data.error_description ?? data.msg ?? 'Erro ao entrar' }
  sessionStorage.setItem(SESSION_KEY, data.access_token)
  return { error: null, email: data.user?.email as string }
}

export async function adminSignOut() {
  const token = sessionStorage.getItem(SESSION_KEY)
  sessionStorage.removeItem(SESSION_KEY)
  if (token) {
    await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
      method: 'POST',
      headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}` },
    }).catch(() => {})
  }
}

export async function adminResetPassword() {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: SUPABASE_KEY },
    body: JSON.stringify({ email: 'immigrantshield@gmail.com' }),
  })
  return res.ok ? { error: null } : { error: 'Erro ao enviar e-mail de redefinição' }
}

export async function adminRpc<T = unknown>(
  fn: string,
  params: Record<string, unknown> = {},
): Promise<{ data: T | null; error: string | null }> {
  const token = sessionStorage.getItem(SESSION_KEY)
  if (!token) return { data: null, error: 'Não autenticado' }
  try {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${fn}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(params),
    })
    // Use text() first — some RPCs return void (204 / empty body).
    // Calling .json() on an empty body throws; .text() is always safe.
    let text = ''
    try { text = await res.text() } catch { /* network body read error */ }
    let json: Record<string, unknown> = {}
    if (text.trim()) {
      try { json = JSON.parse(text) } catch { /* malformed JSON — treat as empty */ }
    }
    if (!res.ok) {
      const msg = (json.message ?? json.hint ?? json.error ?? `HTTP ${res.status}`) as string
      return { data: null, error: msg }
    }
    return { data: (text.trim() ? json as T : null), error: null }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    return { data: null, error: `Erro de rede: ${msg}` }
  }
}

/** Call a Supabase Edge Function with the admin JWT */
export async function adminFn<T = unknown>(
  fn: string,
  body: Record<string, unknown> = {},
): Promise<{ data: T | null; error: string | null }> {
  const token = sessionStorage.getItem(SESSION_KEY)
  if (!token) return { data: null, error: 'Não autenticado' }
  try {
    // Use the admin JWT (has email in payload) — required by functions that
    // do server-side email validation (e.g. stripe-backfill superadmin check).
    const res = await fetch(`${SUPABASE_URL}/functions/v1/${fn}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    })
    const text = await res.text()
    let json: unknown
    try { json = JSON.parse(text) } catch { json = { message: text } }
    if (!res.ok) {
      const j = json as Record<string, unknown>
      return { data: null, error: `HTTP ${res.status}: ${j?.message ?? j?.error ?? text}` }
    }
    return { data: json as T, error: null }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    return { data: null, error: `Erro de rede: ${msg}` }
  }
}

export function adminGetEmail(): string | null {
  const token = sessionStorage.getItem(SESSION_KEY)
  if (!token) return null
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.email ?? null
  } catch { return null }
}
